import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from './api'
import logger from '@/utils/logger'
import type { AiConversation, AiMessage, AiTriageResult } from '@/types'

export const useAiChatStore = defineStore('aiChat', () => {
  const conversations = ref<AiConversation[]>([])
  const selectedConversationId = ref<string | null>(null)
  const messages = ref<AiMessage[]>([])
  const isOpen = ref(false)
  const loading = ref(false)
  const messagesLoading = ref(false)
  const isSending = ref(false)

  const selectedConversation = computed(() =>
    conversations.value.find(c => c.id === selectedConversationId.value) || null
  )

  async function fetchConversations() {
    loading.value = true
    try {
      const res = await api.get('/ai/conversations')
      conversations.value = res.data.data || []
    } catch (err) {
      logger.error('Failed to fetch AI conversations', err)
    } finally {
      loading.value = false
    }
  }

  async function fetchMessages(conversationId: string) {
    messagesLoading.value = true
    try {
      const res = await api.get(`/ai/conversations/${conversationId}`)
      messages.value = res.data.data?.messages || []
    } catch (err) {
      logger.error('Failed to fetch AI messages', err)
    } finally {
      messagesLoading.value = false
    }
  }

  async function selectConversation(id: string) {
    selectedConversationId.value = id
    await fetchMessages(id)
  }

  async function startConversation(message: string, contextType?: string, contextId?: string) {
    try {
      // Create conversation
      const res = await api.post('/ai/conversations', {
        context_type: contextType || null,
        context_id: contextId || null,
      })
      const conv = res.data.data
      conversations.value.unshift(conv)
      selectedConversationId.value = conv.id
      messages.value = []

      // Send first message
      await sendMessage(message)
    } catch (err) {
      logger.error('Failed to start AI conversation', err)
      messages.value.push({
        id: crypto.randomUUID(),
        conversation_id: selectedConversationId.value || '',
        role: 'assistant',
        content: 'Failed to start conversation. The AI service may be unavailable.',
        tool_calls: null,
        tokens_used: null,
        response_time_ms: null,
        created_at: new Date().toISOString(),
        isError: true,
      })
    }
  }

  async function sendMessage(message: string) {
    if (!selectedConversationId.value || isSending.value) return

    isSending.value = true

    // Optimistically add user message
    const userMsg: AiMessage = {
      id: crypto.randomUUID(),
      conversation_id: selectedConversationId.value,
      role: 'user',
      content: message,
      tool_calls: null,
      tokens_used: null,
      response_time_ms: null,
      created_at: new Date().toISOString(),
    }
    messages.value.push(userMsg)

    try {
      const res = await api.post(`/ai/conversations/${selectedConversationId.value}/messages`, {
        message,
      }, {
        timeout: 180000, // 3 min — LLM agent loop with tool calls on remote GPU
      })

      const data = res.data.data

      // Add assistant response
      const assistantMsg: AiMessage = {
        id: crypto.randomUUID(),
        conversation_id: selectedConversationId.value!,
        role: 'assistant',
        content: data.content || '',
        tool_calls: data.tool_calls || null,
        tokens_used: data.tokens_used || null,
        response_time_ms: data.response_time_ms || null,
        created_at: new Date().toISOString(),
      }
      messages.value.push(assistantMsg)

      // Update conversation title in list (may have changed on first message)
      await fetchConversations()
    } catch (err) {
      logger.error('Failed to send AI message', err)
      // Add error message
      messages.value.push({
        id: crypto.randomUUID(),
        conversation_id: selectedConversationId.value!,
        role: 'assistant',
        content: 'Failed to get a response. The AI service may be unavailable.',
        tool_calls: null,
        tokens_used: null,
        response_time_ms: null,
        created_at: new Date().toISOString(),
        isError: true,
      })
    } finally {
      isSending.value = false
    }
  }

  async function deleteConversation(id: string) {
    try {
      await api.delete(`/ai/conversations/${id}`)
      conversations.value = conversations.value.filter(c => c.id !== id)
      if (selectedConversationId.value === id) {
        selectedConversationId.value = null
        messages.value = []
      }
    } catch (err) {
      logger.error('Failed to delete AI conversation', err)
    }
  }

  function toggle() {
    isOpen.value = !isOpen.value
  }

  return {
    conversations,
    selectedConversationId,
    messages,
    isOpen,
    loading,
    messagesLoading,
    isSending,
    selectedConversation,
    fetchConversations,
    fetchMessages,
    selectConversation,
    startConversation,
    sendMessage,
    deleteConversation,
    toggle,
  }
})
